import { useContext, useEffect } from "react";
import { type V2Bag, type V2Value } from "@/features/applications/api/applications.api";
import { Input } from "@/components/ui/input";
import { type FieldDef, f, sel, req, radio } from "../model";
import { OPT } from "../constants";
import { PRICING_PLANS, optionsFor, baseRateLinesFor } from "../pricing";
import { ValidationContext } from "../validation";
import { useReadOnly } from "../readonly";
import { SectionCard, ReqBadge } from "../primitives";
import { FieldInput } from "../FieldInput";
import { CardedFields } from "../FieldGrid";

export function FeesStep({
  feeFields, bag, onChange,
}: {
  feeFields: FieldDef[];
  bag: V2Bag;
  onChange: (key: string, value: V2Value) => void;
}) {
  const { missing, markFilled } = useContext(ValidationContext);
  const readOnly = useReadOnly();
  const proc = typeof bag.processingType === "string" ? bag.processingType : "";
  const plan = typeof bag.pricingPlan === "string" ? bag.pricingPlan : "";
  const option = typeof bag.pricingPlanOption === "string" ? bag.pricingPlanOption : "";
  const opts = optionsFor(proc, plan);

  useEffect(() => {
    if (readOnly) return;
    if (opts.length === 0) {
      if (option !== "") onChange("pricingPlanOption", null);
    } else if (!opts.includes(option)) {
      onChange("pricingPlanOption", opts[0]);
    }

  }, [proc, plan, readOnly]);

  const left: FieldDef[] = [
    radio(req(sel("processingType", "Processing type", [...OPT.processingType]))),
    req(sel("pricingPlan", "Pricing plan", [...PRICING_PLANS])),
    ...(opts.length > 0 ? [req(sel("pricingPlanOption", "Option", opts))] : []),
    f("includeDailyDiscount", "Daily Discount", "yesno"),
    f("sameDayAch", "Same Day Funding", "yesno"),
  ];

  const numInput = (key: string, required: boolean) => {
    const isMissing = !readOnly && missing.has(key);
    return (
      <Input
        type="number"
        min={0}
        disabled={readOnly}
        data-missing={isMissing ? "true" : undefined}
        className={isMissing ? "border-destructive bg-destructive/5" : !readOnly && required ? "border-amber-300 bg-amber-50/70" : undefined}
        value={typeof bag[key] === "number" ? (bag[key] as number) : ""}
        onChange={(e) => {
          if (e.target.value === "") {
            onChange(key, null);
            markFilled(key);
            return;
          }
          const n = Number(e.target.value);
          if (Number.isNaN(n) || n < 0) return;
          onChange(key, n);
          markFilled(key);
        }}
      />
    );
  };

  const rest = feeFields.filter((d) => d.section !== "Pricing plan" && d.section !== "Base rates");

  return (
    <>
      <SectionCard title="Pricing plan">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
          <div className="space-y-4">
            {left.map((d) => (
              <FieldInput key={d.key} def={d} value={bag[d.key] ?? null} onChange={(v) => onChange(d.key, v)} />
            ))}
          </div>
          <div className="lg:border-l lg:border-slate-100 lg:pl-8">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Base Rates</h4>
            <div className="space-y-3">
              {baseRateLinesFor(proc, plan, option).map((line) => (
                <div key={line.pctKey} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{line.label}</span>
                      {line.required && !readOnly && <ReqBadge />}
                    </div>
                    <div className="flex items-center gap-2">
                      {numInput(line.pctKey, line.required)}
                      <span className="text-xs text-slate-500">%</span>
                    </div>
                  </div>
                  {line.perItemKey ? (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Per Item Fee $</div>
                      {numInput(line.perItemKey, false)}
                    </div>
                  ) : (
                    <div className="hidden sm:block" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>
      <CardedFields fields={rest} bag={bag} onChange={onChange} />
    </>
  );
}
